import { defineStore } from 'pinia'
import { computed } from 'vue'

import { useProxyConfig } from '../../composables/use-proxy-config'
import { createResettableLocalStorage, createResettableRef } from '../../utils/resettable'
import { useProvidersStore } from '../providers'

export const useConsciousnessStore = defineStore('consciousness', () => {
  const providersStore = useProvidersStore()
  const proxyConfig = useProxyConfig()

  // Proxy mode detection
  const isProxyMode = computed(() => proxyConfig.isProxyEnabled())

  // State - local storage only used when proxy DISABLED
  const [activeProvider, resetActiveProvider] = createResettableLocalStorage('settings/consciousness/active-provider', '')
  const [activeModel, resetActiveModel] = createResettableLocalStorage('settings/consciousness/active-model', '')
  const [activeCustomModelName, resetActiveCustomModelName] = createResettableLocalStorage('settings/consciousness/active-custom-model', '')
  const [expandedDescriptions, resetExpandedDescriptions] = createResettableRef<Record<string, boolean>>({})
  const [modelSearchQuery, resetModelSearchQuery] = createResettableRef('')

  // Effective values
  // When proxy enabled: provider ALWAYS from server, only model is local
  // When proxy disabled: all from local storage
  const effectiveProvider = computed(() => {
    if (isProxyMode.value) {
      return proxyConfig.defaultChatProvider.value || ''
    }
    return activeProvider.value
  })

  const effectiveModel = computed(() => {
    // Model selection is ALWAYS local (user choice)
    if (isProxyMode.value && !activeModel.value) {
      // Use server default only if user hasn't selected anything
      return proxyConfig.defaultChatModel.value || ''
    }
    return activeModel.value
  })

  // Computed properties
  const supportsModelListing = computed(() => {
    return providersStore.getProviderMetadata(activeProvider.value)?.capabilities.listModels !== undefined
  })

  const providerModels = computed(() => {
    return providersStore.getModelsForProvider(activeProvider.value)
  })

  const isLoadingActiveProviderModels = computed(() => {
    return providersStore.isLoadingModels[activeProvider.value] || false
  })

  const activeProviderModelError = computed(() => {
    return providersStore.modelLoadError[activeProvider.value] || null
  })

  const filteredModels = computed(() => {
    if (!modelSearchQuery.value.trim()) {
      return providerModels.value
    }

    const query = modelSearchQuery.value.toLowerCase().trim()
    return providerModels.value.filter(model =>
      model.name.toLowerCase().includes(query)
      || model.id.toLowerCase().includes(query)
      || (model.description && model.description.toLowerCase().includes(query)),
    )
  })

  function resetModelSelection() {
    resetActiveModel()
    resetActiveCustomModelName()
    resetExpandedDescriptions()
    resetModelSearchQuery()
  }

  async function loadModelsForProvider(provider: string) {
    if (provider && providersStore.getProviderMetadata(provider)?.capabilities.listModels !== undefined) {
      await providersStore.fetchModelsForProvider(provider)
    }
  }

  async function getModelsForProvider(provider: string) {
    if (provider && providersStore.getProviderMetadata(provider)?.capabilities.listModels !== undefined) {
      return providersStore.getModelsForProvider(provider)
    }

    return []
  }

  const configured = computed(() => {
    // Always check effective values (which use server config when proxy enabled)
    return !!effectiveProvider.value && !!effectiveModel.value
  })

  function resetState() {
    resetActiveProvider()
    resetModelSelection()
  }

  return {
    // Proxy mode
    isProxyMode,

    // State
    configured,
    activeProvider,
    activeModel,
    customModelName: activeCustomModelName,
    expandedDescriptions,
    modelSearchQuery,

    // Effective values (use server defaults when proxy enabled)
    effectiveProvider,
    effectiveModel,

    // Computed
    supportsModelListing,
    providerModels,
    isLoadingActiveProviderModels,
    activeProviderModelError,
    filteredModels,

    // Actions
    resetModelSelection,
    loadModelsForProvider,
    getModelsForProvider,
    resetState,
  }
})
